import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '@iconify/react';
import bx from '@iconify-json/bx/icons.json';
import { useAuth } from '../../contexts/AuthContext';
import { useCampaigns } from '../../hooks/useCampaigns';
import { useLeadsForCampaign } from '../../hooks/useLeadsForCampaign';
import { useTeamPreferences } from '../../hooks/useTeamPreferences';
import { useUserPreferences } from '../../hooks/useUserPreferences';
import type { CampaignMetadata, MinimalLeadData } from '../../types';
import { renderSqlReport, type SqlReport } from './sql-report';
import { renderCampaignReport, type CampaignReport } from './campaign-report';
import { getTemplate } from './registry';
import { buildCampaignTranslations, hasCampaignTranslations } from './campaign-i18n';
import { translationCacheKey, getCachedTranslation, setCachedTranslation } from './translation-cache';
import { translateReport, publishReport, saveReportRecord, getTemplateConfig, type ReportResult } from './report-service';
import { type SectionConfig, DEFAULT_CONFIG } from './section-catalog';
import { normalizeCampaignContent } from './campaign-config';
import { getNormalizedStatusKey } from '../../utils/statusMapping';
import { buildExportRows, toCsv, toXlsx, downloadBlob, slugify } from './templates/campaign-export/export';
import { n8nService } from '../../components/core/services/n8n.service';
import { leadToReport, campaignToReport } from '../../integrations/reports/reportDataAdapter';
import { fetchFullLeadFromN8N } from '../../utils/n8nDataFetchers';
import './preview.css';
import { SellerReportManager } from './SellerReportManager';
import { TranslationEffect } from './TranslationEffect';

const glyph = (name: keyof typeof bx.icons) => ({...bx.icons[name],width:24,height:24});
// The GLOBAL document-language list — one platform-wide dropdown for EVERY
// template (per the template architecture; conf carries no languages field).
// Top 25 languages by global business relevance — worldwide platform coverage.
const LANGUAGES = ['en','es','fr','de','pt','it','nl','sv','da','nb','fi','pl','ru','uk','el','tr','ar','he','hi','zh','ja','ko','id','vi','ca'];
const TRANSLATION_COST = 0.25;

type LeadTemplateId = 'lead-mql' | 'lead-sql';
type CampaignTemplateId = 'campaign-overview' | 'campaign-companies' | 'campaign-leads';
// Template picker cards read from the REGISTRY (each template folder's conf) —
// name and description live with the template, not here. Icons stay UI-side.
const TEMPLATE_ICONS: Record<string, keyof typeof bx.icons> = { 'lead-mql':'user', 'lead-sql':'time', 'campaign-overview':'chart', 'campaign-companies':'building', 'campaign-leads':'bxs-group', 'campaign-export':'download' };
// Pickers are derived from the REGISTRY in registry order — the SAME list and
// order the Admin → Reports tabs show. (ALWAYS-HAS-TO-MATCH rule.)
const LEAD_TEMPLATES = (['lead-sql','lead-mql'] as const).map(id => {
  const t = getTemplate(id)!;
  return { id, name: t.conf.name === 'Lead MQL' ? 'Lead Intelligence Report (MQL)' : 'Sales Intelligence Report (SQL)', description: t.conf.description, icon: TEMPLATE_ICONS[id] };
});
// Campaign picker: the registered campaign templates — default first, the
// '+ Lead List' render option of campaign-overview second, then the new
// directory variants, then the export.
// Campaign picker = registry document templates (sourceKinds includes
// 'campaign'), SAME ids/names/order as the Admin → Reports → Campaigns tab.
// 'Campaign Export' is kind 'export' — exposed as the Export data dropdown,
// matching its Admin card (xlsx · csv).
const CAMPAIGN_TEMPLATES = (['campaign-overview','campaign-companies','campaign-leads'] as const).map(id => {
  const t = getTemplate(id)!;
  return { id, name: t.conf.name, description: t.conf.description, icon: TEMPLATE_ICONS[id] };
});

function download(content: string, filename: string, type: string) { const url=URL.createObjectURL(new Blob([content],{type})); const a=document.createElement('a');a.href=url;a.download=filename;a.click();setTimeout(()=>URL.revokeObjectURL(url),1000); }

/** Modes: full picker (default), campaign-locked (Team view +Report), lead-locked (ISR +Report). */
export interface ReportPreviewManagerProps {
  lockCampaignId?: string;
  sellerId?: string;
  sellerName?: string;
  initialCategory?: 'sellers';
  lockLeadId?: string | number;
  /** Preselect a campaign template (deep link, e.g. the card Export action). */
  initialCampaignTemplate?: string;
  onBack?: () => void;
}

export function ReportPreviewManager(props: ReportPreviewManagerProps = {}) {
 const [sellerMode,setSellerMode] = useState(!!props.sellerId || props.initialCategory === 'sellers');
 return sellerMode ? <SellerReportManager sellerId={props.sellerId} sellerName={props.sellerName} onBack={props.onBack}/> : <LeadCampaignReportManager {...props} onChooseSellers={()=>setSellerMode(true)}/>;
}

function LeadCampaignReportManager({ lockCampaignId, lockLeadId, initialCampaignTemplate, onBack, onChooseSellers }: ReportPreviewManagerProps & {onChooseSellers: () => void}) {
 const { currentUser, targetEmail } = useAuth();
 const userEmail = targetEmail || currentUser?.email;
 const { data: campaigns } = useCampaigns();
 const prefsQuery = useTeamPreferences();
 const userPrefsQuery = useUserPreferences();
 const branding = useMemo(() => ({
   name: prefsQuery.data?.team_name || 'Brainiacs',
   color: prefsQuery.data?.brand_primary_colour || '#7C4DFF',
   logoUrl: prefsQuery.data?.logo_url || undefined,
 }), [prefsQuery.data]);

 const [campaignId,setCampaignId]=useState(lockCampaignId || '');
 const [leadId,setLeadId]=useState(lockLeadId!=null?String(lockLeadId):'');
 const [leadTemplate,setLeadTemplate]=useState<LeadTemplateId>('lead-sql');
 // Deep-link preselect validated against the registry's PDF templates.
 const [campaignTemplate,setCampaignTemplate]=useState<CampaignTemplateId>(
   initialCampaignTemplate && getTemplate(initialCampaignTemplate)?.conf.sourceKinds.includes('campaign') && getTemplate(initialCampaignTemplate)!.conf.kind !== 'export'
     ? initialCampaignTemplate as CampaignTemplateId
     : 'campaign-companies');
 // Deep link straight to the export (route ?template=campaign-export): opens the
 // export flow without it being a template option.
 const [exportStatus,setExportStatus]=useState<{working:boolean;message:string;error:string}|null>(null);
 const [exportDropdownOpen,setExportDropdownOpen]=useState(false);
 const [language,setLanguage]=useState('en');
 // The user's Appwrite language pref (async) drives the default once loaded.
 // Prefs often arrive AFTER the payload is built (cached query) — changing the
 // state alone would leave an English document under a non-English dropdown,
 // so route it through chooseLanguage (same cache/translate path as a click).
 useEffect(()=>{
   const pref=(userPrefsQuery.data?.language||'').split('-')[0].toLowerCase();
   if(!pref) return;
   setLanguage(pref);
   if(pref!=='en') void chooseLanguage(pref);
 },[userPrefsQuery.data?.language]);
 const [translations,setTranslations]=useState<Record<string,string>|undefined>();
 const [error,setError]=useState('');
 const [warnings,setWarnings]=useState<string[]>([]);
 const [job,setJob]=useState<ReportResult|null>(null);
 const [phase,setPhase]=useState<'idle'|'building'|'translating'|'generating'>('idle');
 const [saved,setSaved]=useState(false);
 const [saveFailed,setSaveFailed]=useState(false);
 // The browser refused even the click-time tab (aggressive popup blockers) —
 // the result note tells the user to click "Open PDF Report" instead.
 const [popupBlocked,setPopupBlocked]=useState(false);
 const request=useRef<{signature:string;id:string}|null>(null);
 const frame=useRef<HTMLIFrameElement>(null);
 const campaignLocked = !!lockCampaignId;
 const leadLocked = !!lockLeadId;
 const [selectedSourceType,setSelectedSourceType] = useState<'lead'|'campaign'>('lead');
 const sourceType: 'lead'|'campaign' = leadLocked ? 'lead' : campaignLocked ? 'campaign' : selectedSourceType;
 // Publish/translate under an id the DEPLOYED proxy accepts
// (lead-mql | lead-sql | campaign-overview). The directory variants are
// CLIENT-side renders of the campaign-overview payload — each gets a distinct
// storage file id via the variant tag in the payload's reference (-co / -ld),
// so they never overwrite each other or the overview PDF.
const DEPLOYED_TEMPLATE_IDS: Record<string, string> = {
  'campaign-companies': 'campaign-overview',
  'campaign-leads': 'campaign-overview',
};
const templateIdForBackend=sourceType==='campaign'
  ?(DEPLOYED_TEMPLATE_IDS[campaignTemplate] ?? campaignTemplate)
  :leadTemplate==='lead-mql'?'lead-mql':'lead-sql';
 const busy=phase!=='idle';
 // Every signed-in user can generate; authorization is enforced server-side
 // by the n8n-proxy on each action.

 useEffect(()=>{ if(campaignLocked && !campaignId) setCampaignId(lockCampaignId!); },[campaignLocked,lockCampaignId,campaignId]);

 const campaign=useMemo(()=>campaigns?.find((c:CampaignMetadata)=>c.id===campaignId),[campaigns,campaignId]);
 const leadsQuery=useLeadsForCampaign(campaignId||undefined,undefined,{enabled:!!campaignId});
 const leads=leadsQuery.data||[];
 // Lead-locked mode may open before the per-campaign list cache is warm. The
 // full-record fetch below only needs IDs — construct a minimal stand-in row
 // so `lead` resolves immediately and the payload builder runs.
 const lead=useMemo(()=>{
   const found = leads.find((l:MinimalLeadData)=>String(l.id)===leadId);
   if (found || !leadLocked) return found;
   return { id: leadId, campaignId: campaignId, campaignName: campaign?.name || '', leadName: '', companyName: '', status: '' } as unknown as MinimalLeadData;
 },[leads,leadId,leadLocked,campaignId,campaign]);

 const [built,setBuilt]=useState<{payload:any;texts:Record<string,string>;warnings:string[];leadId:string;campaignId:string;campaignName:string}|null>(null);
 // Admin-configured section visibility/order (fetched once; falls back to defaults)
 const [templateConfigs,setTemplateConfigs]=useState<Record<string,SectionConfig>>({});
 const templateConfigsRef=useRef(templateConfigs);
 templateConfigsRef.current=templateConfigs;
 const [sectionConfig,setSectionConfig]=useState<SectionConfig|undefined>(undefined);
 // Which template the loaded sectionConfig belongs to. Synced with the CURRENT
 // selection below — the fetch itself runs ONCE (the config document holds both
 // lead templates; re-fetching per template only added state churn).
 const [configTemplate,setConfigTemplate]=useState('');
 const [configError,setConfigError]=useState('');
 const configLoaded=useRef(false);
 useEffect(()=>{
   // The admin template setup is LIVE: refetch on mount AND whenever the window
   // regains focus/visibility. The old once-per-mount guard meant edits saved in
   // Admin → Reports (another tab, or while the Studio stayed mounted) were
   // ignored for the whole session — reports silently rendered the stale setup.
   // Setters below are safe after unmount (no-op at worst). NO live-cancellation
   // of the fetch itself: discarding the result on remount left configTemplate ''
   // and the build guard blocked EVERY report (the blank-report regression).
   const apply=(cfg:Record<string,SectionConfig>|null, message='')=>{
     configLoaded.current=true;
     const key=leadTemplate==='lead-mql'?'lead-mql':'lead-sql';
     const next: Record<string,SectionConfig> = cfg ?? { 'lead-sql': DEFAULT_CONFIG, 'lead-mql': DEFAULT_CONFIG };
     // Ignore no-op refreshes: updating state on every focus would rebuild the
     // payload (and re-fetch the lead from n8n) for nothing.
     if(templateConfigsRef.current && Object.keys(templateConfigsRef.current).length && JSON.stringify(next)===JSON.stringify(templateConfigsRef.current)){ if(message) setConfigError(message); return; }
     templateConfigsRef.current=next;
     setTemplateConfigs(next);
     sectionConfigRef.current=next[key] ?? DEFAULT_CONFIG;
     setSectionConfig(sectionConfigRef.current);
     setConfigTemplate(sourceType === 'campaign' ? campaignTemplate : leadTemplate);
     // A successful load invalidates any previous load-failure warning.
     if(message) setConfigError(message); else setConfigError('');
   };
   const load=()=>{
     getTemplateConfig()
       .then(cfg=>apply(cfg))
       .catch(()=>{
         // NEVER blank the report on a config failure — the setup is an
         // enhancement. Render with defaults and surface a warning (only
         // when nothing usable is loaded yet).
         apply(null,'Report template settings could not be loaded — the report uses the default layout.');
       });
   };
   load();
   const onVisible=()=>{ if(document.visibilityState==='visible') load(); };
   window.addEventListener('focus', onVisible);
   document.addEventListener('visibilitychange', onVisible);
   return ()=>{ window.removeEventListener('focus', onVisible); document.removeEventListener('visibilitychange', onVisible); };
 },[leadTemplate,campaignTemplate,sourceType]);

 // Keep sectionConfig + configTemplate in sync with the SELECTED template
 // (swap lead-sql ↔ lead-mql) without refetching. Until the config document
 // resolves, configTemplate stays '' so the build effect WAITS instead of
 // rendering with default sections (that race produced PDFs that ignored the
 // admin setup when the lead fetch beat the config fetch).
 const selectedTemplateKey = sourceType==='campaign' ? campaignTemplate : leadTemplate;
 const sectionConfigRef=useRef<SectionConfig|undefined>(undefined);
 useEffect(()=>{
   if(!configLoaded.current || !Object.keys(templateConfigs).length)return;
   const key=sourceType==='lead' ? (leadTemplate==='lead-mql'?'lead-mql':'lead-sql') : leadTemplate;
   const next=templateConfigs[key] ?? DEFAULT_CONFIG;
   sectionConfigRef.current=next;
   setSectionConfig(next);
   setConfigTemplate(selectedTemplateKey);
 },[selectedTemplateKey,templateConfigs,sourceType,leadTemplate]);

 // Build the live payload whenever the resolved source/template changes.
 // LANGUAGE IS NOT A DEPENDENCY: the payload is language-independent — only
 // translations are. `chooseLanguage` owns the language flow (cache → AI call →
 // overlay), so a language change must NOT rebuild: rebuilding blanked the
 // preview and re-fetched the lead from n8n for seconds BEFORE the translating
 // overlay appeared. The translate branch below still runs on a genuine build
 // so a deep link opening straight into a non-English language translates once.
 useEffect(()=>{
   let live=true;
   setBuilt(null);setWarnings([]);setJob(null);setSaved(false);setSaveFailed(false);setPopupBlocked(false);request.current=null;setError('');
   setTranslations(undefined);
   if(configTemplate !== (sourceType === 'campaign' ? campaignTemplate : leadTemplate)){setPhase('idle');setError(configError);return;}
   if (sourceType === 'campaign' && templateConfigsRef.current[campaignTemplate]?.deleted) { setPhase('idle'); setError('This template has been deleted. Choose another report type.'); return; }
   const capturedAt=new Date().toISOString();
   const campaignName = campaign?.name || '';
   try {
     if(sourceType==='campaign'){
       if(!campaign || leadsQuery.isLoading){setPhase('idle');return;}
       // Directory variant + pipeline slice per campaign template — BOTH come
       // from the admin-saved content config (statuses the admin ticked drive
       // which leads appear). The export template skips the payload build.
       // The OVERVIEW is the pipeline page only (overviewOnly render flag): an
       // admin-saved directory config must never leak into its payload, or the
       // overview renders as the companies/leads variant AND publishes at that
       // variant's `-co`/`-ld` file id, overwriting its PDF.
       const content = normalizeCampaignContent(templateConfigs[campaignTemplate]?.campaign,campaignTemplate);
       const directorySpec = campaignTemplate==='campaign-overview' || content.directory === 'none' ? undefined
         : { directory: content.directory as 'companies'|'leads', statuses: content.statuses };
       // Opportunity digest lines are opt-in per template — the overview never
       // includes them (same pipeline-page rule as the directory).
       const opportunityTypes = campaignTemplate==='campaign-overview' ? [] : content.opportunities;
       // Opportunity HYDRATION: the cards payload does not carry the
       // opportunity columns — the SAME get_lead call the lead report uses
       // does. Opportunities only exist AFTER enrichment, so we hydrate ONLY
       // enriched→engaged rows that the directory will render (typically a few
       // dozen per campaign — never the whole list) with a bounded pool, and
       // merge ONLY the opportunity fields into the card rows. Non-fatal per
       // lead: a failed fetch just means that row renders without its line.
       const OPPORTUNITY_STATUSES: readonly string[] = ['enriched','outreaching','outreached','engaged'];
       const buildFrom = async (): Promise<MinimalLeadData[]> => {
         // No directory → no rows to carry a digest line → nothing to hydrate.
         if(!opportunityTypes.length || !directorySpec || !leads.length) return leads;
         // Hydration takes a few seconds (bounded get_lead pool) — the same
         // overlay the translation flow uses covers the blank paper.
         if(live) setPhase('building');
         const hydratable = leads.filter(l => {
           const status = getNormalizedStatusKey(l.status);
           return OPPORTUNITY_STATUSES.includes(status) && (directorySpec ? (directorySpec.statuses as readonly string[]).includes(status) : true);
         });
         const seenCompanies=new Set<string>();
         const targets = (directorySpec?.directory === 'companies'
           ? hydratable.filter(l => { const k = String(l.companyName||'').trim().toLowerCase(); if(!k || seenCompanies.has(k)) return false; seenCompanies.add(k); return true; })
           : hydratable);
         const hydrated=new Map<string,MinimalLeadData>();
         let cursor=0;
         await Promise.all(Array.from({length:Math.min(6,targets.length)},async()=>{
           while(cursor<targets.length){
             const l=targets[cursor++];
             try {
               const full = await fetchFullLeadFromN8N(String(l.id), String(campaign.id), userEmail!);
               if(full && (full.campaignOpportunities?.length || full.generalOpportunities?.length || full.opportunities?.length)) {
                 hydrated.set(String(l.id), { ...l, campaignOpportunities: full.campaignOpportunities, generalOpportunities: full.generalOpportunities ?? full.opportunities });
               }
             } catch { /* non-fatal: row renders without its opportunity line */ }
           }
         }));
         if(live && hydrated.size) setWarnings(w=>[...w,'Opportunity lines were built from the leads fetched just now — regenerate after enrichment for the latest detections.']);
         return leads.map(l => hydrated.get(String(l.id)) ?? l);
       };
       void buildFrom().then(hydratedLeads=>{
         if(!live)return;
         const result=campaignToReport(campaign,hydratedLeads,branding,{withLeadList:!!directorySpec,directory:directorySpec?.directory,directoryStatuses:directorySpec?.statuses,opportunityTypes,capturedAt});
         const template=getTemplate('campaign-overview')!;
         const texts=template.translationTexts(result.report);
         setBuilt({payload:result.report,texts,warnings:result.warnings,leadId:'',campaignId:result.campaignId,campaignName});
         // Campaign labels are fixed platform vocabulary — apply the local
         // dictionary HERE (like the lead branch applies translations), because
         // the builder resets translations on every run. Without this, choosing a
         // language or switching template would wipe the translations.
         // `buildCampaignTranslations` owns the merge rules (it narrows the
         // dictionary to the keys this report emits, or validation would throw on
         // a stage the campaign lacks). Generation translates pipeline definitions
         // and opportunity prose through the shared translation service.
         const { translations:dictTr }=buildCampaignTranslations(result.report,language,texts);
         if(dictTr) setTranslations(dictTr);
         setPhase('idle');
       }).catch(e=>{ if(live){ setError(e instanceof Error?e.message:'This campaign could not be converted into a report.'); setPhase('idle'); } });
       // The async chain owns the phase from here (building → idle) — the
       // sync fallthrough below must not clobber the overlay mid-fetch.
       return;
     } else if(campaign && lead){
       void buildLeadPayload(lead,capturedAt).catch(error=>{if(live){setError(error instanceof Error ? error.message : 'Could not build report.');setPhase('idle');}});
       return;
     }
     setPhase('idle');
   } catch(e){ if(live){setError(e instanceof Error?e.message:'This source could not be converted into a report.');setPhase('idle');} }
   async function buildLeadPayload(cardLead:MinimalLeadData,captured:string){
     // The lead adapter needs the full n8n envelope (lvl1/lvl2/intelligence).
     // Fetch it through the same get_lead pipeline the ISR workspace uses; the
     // card row still carries identity basics if that fetch fails.
     let mapped:any = cardLead;
     try { mapped = await fetchFullLeadFromN8N(String(cardLead.id), String(campaign!.id), userEmail!); }
     catch {
       mapped = cardLead;
       if (live) setWarnings(w => [...w, 'The full lead record could not be loaded — the preview shows identity only. Try again for the complete briefing.']);
     }
     const result=leadToReport({lead:mapped,branding,campaignName:campaign!.name,timezone:prefsQuery.data?.timezone,capturedAt:captured},leadTemplate==='lead-mql'?'mql':'sql',sectionConfigRef.current ?? sectionConfig);
     if(!live)return;
     const template=getTemplate('lead-sql')!;
     const texts=template.translationTexts(result.report);
     setBuilt({payload:result.report,texts,warnings:result.warnings,leadId:result.leadId,campaignId:result.campaignId,campaignName});
     // Non-English viewers: reuse the cached translation, or translate once and
     // cache it. The key includes a fingerprint of the SOURCE TEXTS, so a
     // template-config change (or lead re-enrichment) is a guaranteed cache
     // MISS — a stale block can never be served, and `validateTranslations`
     // can never silently kick the PDF back to English. Session-scoped:
     // survives remounts and route changes, dies on reload (the PDF file id
     // already covers reloads).
     if(!language.startsWith('en')){
       const cacheKey=translationCacheKey(result.leadId,leadTemplate,language,texts);
       const cachedTr=getCachedTranslation(cacheKey);
       if(cachedTr){ if(live) setTranslations(cachedTr); return; }
       if(live) setPhase('translating');
       translateReport('lead-sql',result.report,language)
         .then(tr=>{
           if(!live||!tr.translations)return;
           setCachedTranslation(cacheKey,tr.translations);
           setTranslations(tr.translations);
           setPhase('idle');
         })
         .catch(err=>{ if(live){ setError(err instanceof Error?err.message:'Translation failed.'); setPhase('idle'); } });
     }
   }
 },[sourceType,campaign,lead,leadTemplate,campaignTemplate,leads,leadsQuery.isLoading,branding,sectionConfig,configTemplate,configError]);


 const sourceReady=!!campaign && (sourceType==='campaign' || !!lead);
 // Previously-saved report URL for this lead (report_lead_url on the lvl2 row,
 // surfaced through the card payload) — re-opening shows the existing PDF.
 const existingLeadReportUrl=useMemo(()=>{
   if(sourceType!=='lead'||!lead) return null;
   const u=(lead as any).reportLeadUrl ?? (lead as any).personLvl2?.report_lead_url;
   return typeof u==='string'&&u.trim().length>0?u.trim():null;
 },[lead,sourceType]);
 const needsTranslation=!language.startsWith('en');

 const chooseLanguage=async(next:string)=>{
   setLanguage(next);setJob(null);
   if(!built){setTranslations(undefined);return;}
   // Campaign reports: the builder applies the local dictionary (it owns the
   // translate step so a rebuild can't wipe it) — nothing to do here.
   if(sourceType==='campaign') return;
   if(next.startsWith('en')){setTranslations(undefined);return;}
   // Lead reports: same fingerprinted session cache as the builder — switching
   // languages back and forth costs ONE AI call per unique content+language,
   // not one per click.
   setPhase('translating');setError('');
   const texts=getTemplate('lead-sql')!.translationTexts(built.payload as SqlReport);
   const cacheKey=translationCacheKey(String((built.payload as SqlReport).reference||''),leadTemplate,next,texts);
   const cachedTr=getCachedTranslation(cacheKey);
   if(cachedTr){ setTranslations(cachedTr);setPhase('idle');return; }
   try {
     const result=await translateReport(templateIdForBackend,built.payload,next);
     if(result.translations) setCachedTranslation(cacheKey,result.translations);
     setTranslations(result.translations);
   } catch(e){ setError(e instanceof Error?e.message:'Translation failed.');setTranslations(undefined); }
   finally{ setPhase('idle'); }
 };

 const templateCards=sourceType==='lead'?LEAD_TEMPLATES:CAMPAIGN_TEMPLATES;
 const activeTemplateId=sourceType==='lead'?leadTemplate:campaignTemplate;
 // One GLOBAL language dropdown for every template (template architecture).
 const languages=LANGUAGES;

 // DATA EXPORT (campaigns): dropdown choice → fetch raw leads_cards rows →
 // map to the 50-column contract → download. Fully inline — no preview view,
 // no second click. Status shows in place under the button.
 const runExport=async(format:'xlsx'|'csv')=>{
   if(!campaignId||!userEmail){setError('Select a campaign first.');return;}
   setExportStatus({working:true,message:`Preparing ${format.toUpperCase()} export…`,error:''});
   try {
     const response=await n8nService.getLeadsCards({ campaign_id: campaignId, client_email: userEmail });
     if (!response.success || !response.data) throw new Error(response.error || 'The campaign data could not be loaded.');
     const data: any = response.data;
     const leads = Array.isArray(data?.campaigns)
       ? (data.campaigns.find((c: any) => String(c.campaign_id) === String(campaignId))?.leads ?? [])
       : Array.isArray(data?.leads) ? data.leads : [];
     // SAME scope the report directory uses: the admin-saved statuses.
     const scope = normalizeCampaignContent(templateConfigs[campaignTemplate]?.campaign,campaignTemplate);
     const { rows } = buildExportRows(leads, scope.statuses);
     if (!rows.length) { setExportStatus({working:false,message:'',error:'No leads in the selected statuses for this campaign — nothing to export.'}); return; }
     const base = `${slugify(campaign?.name || 'campaign')}-export`;
     if (format === 'csv') downloadBlob(new Blob([toCsv(rows)], { type: 'text/csv;charset=utf-8' }), `${base}.csv`);
     else downloadBlob(await toXlsx(rows), `${base}.xlsx`);
     setExportStatus({working:false,message:`${rows.length} lead${rows.length===1?'':'s'} exported — check your downloads.`,error:''});
   } catch(e){ setExportStatus({working:false,message:'',error:e instanceof Error?e.message:'The export could not be generated.'}); }
 };

 const generate=async()=>{
   if(!built || (sourceType==='lead' && configTemplate!==leadTemplate))return;
   // The export template is a client-side download — no PDF publish path.
   if(getTemplate(templateIdForBackend)?.conf.kind==='export')return;
   setPhase('generating');setError('');setSaved(false);
   // POPUP-SAFE TAB: open the tab SYNCHRONOUSLY in the click handler —
   // Chrome only allows window.open within the click's transient activation,
   // which has EXPIRED by the time the translate + Gotenberg chain finishes
   // (the previous approach opened after the awaits and was popup-blocked —
   // the "reports are not working" reports). The tab shows the app while the
   // document generates, then we navigate it to the PDF URL. Deliberately NO
   // noopener: it severs the handle we need for reportTab.location.href (the
   // about:blank becomes cross-origin and the assignment fails), which is the
   // whole point of pre-opening. The tab only ever shows our own storage PDF.
   const reportTab=window.open('about:blank','_blank');
   try {
     // 1) TRANSLATE — lead reports in a non-English language go through the
     //    reusable AI translator. Cached by the deterministic file id: the
     //    cost is paid once per report+language, team-wide.
     let finalTranslations = translations;
     // Labels use the local dictionary. Pipeline definitions and opportunity
     // prose require the shared translator; cache the complete translated block.
     const campaignHasTranslatableProse = sourceType==='campaign' && Object.keys(built.texts).some(key => key.startsWith('stageDescription.') || /^lead\.\d+\.opportunity$/.test(key));
     const campaignDictionary = sourceType==='campaign' && hasCampaignTranslations(language) && !campaignHasTranslatableProse;
     if(sourceType==='campaign' && campaignHasTranslatableProse && !language.startsWith('en')){
       // Pipeline definitions and opportunity prose are text the dictionary can't cover — discard
       // the builder's dictionary-only block and translate the WHOLE document
       // (labels + prose) so cards never render mixed-language. The
       // fingerprinted cache makes re-runs of the same content free.
       finalTranslations=undefined;
       const texts=getTemplate('campaign-overview')!.translationTexts(built.payload);
       const cacheKey=translationCacheKey(String((built.payload as {reference?:string}).reference||''),templateIdForBackend,language,texts);
       const cachedTr=getCachedTranslation(cacheKey);
       if(cachedTr){ finalTranslations=cachedTr; setTranslations(cachedTr); }
       else {
         setPhase('translating');
         const result=await translateReport(templateIdForBackend,built.payload,language);
         finalTranslations=result.translations;
         if(result.translations) setCachedTranslation(cacheKey,result.translations);
         setTranslations(result.translations);
         setPhase('generating');
       }
     }
     else if(sourceType==='lead' && !language.startsWith('en') && !finalTranslations){
       // Cache lookup before spending an AI call — same key the builder and
       // chooseLanguage use, so all three paths share one entry per content.
       const texts=getTemplate('lead-sql')!.translationTexts(built.payload as SqlReport);
       const cacheKey=translationCacheKey(String((built.payload as SqlReport).reference||''),leadTemplate,language,texts);
       const cachedTr=getCachedTranslation(cacheKey);
       if(cachedTr){ finalTranslations=cachedTr; setTranslations(cachedTr); }
       else {
         setPhase('translating');
         const result=await translateReport(templateIdForBackend==='lead-mql'?'lead-mql':'lead-sql',built.payload,language);
         finalTranslations=result.translations;
         if(result.translations) setCachedTranslation(cacheKey,result.translations);
         setTranslations(result.translations);
         setPhase('generating');
       }
     } else if(sourceType==='campaign' && !language.startsWith('en') && !finalTranslations && !campaignDictionary){
       // Uncovered campaign language: every emitted key is one flat text, so the
       // same reusable translator handles it. One AI call per report+language.
       setPhase('translating');
       const result=await translateReport(templateIdForBackend,built.payload,language);
       finalTranslations=result.translations;
       setTranslations(result.translations);
       setPhase('generating');
     }
     // 2) RENDER — client-side, from the approved template (always in memory).
     const html = sourceType==='campaign'
       ? (getTemplate(templateIdForBackend)!.render as any)(built.payload,{language,translations:finalTranslations,content:templateConfigs[campaignTemplate]?.campaign,overviewOnly:campaignTemplate==='campaign-overview'})
       : renderSqlReport(built.payload as SqlReport,{language,translations:finalTranslations,mql:leadTemplate==='lead-mql',showActivity:!sectionConfig||sectionConfig.sections.includes('activity-timeline'),showSummary:!sectionConfig||sectionConfig.sections.includes('interaction-summary')});
     // 3) PUBLISH — convert + upload at the deterministic file id (cached if
     //    it already exists: re-opening never re-translates or re-converts).
     const result=await publishReport(templateIdForBackend,built.payload,language,html);
     setJob(result);
     // Redirect the pre-opened tab to the PDF the moment it is ready. The
     // tab was opened synchronously at click time (see above), so no popup
     // permission is involved here — navigation of an existing tab is never
     // blocked. about:blank + noopener: a closed tab or a null return both
     // mean the browser refused — the sticky bar's "Open PDF Report" link
     // becomes the fallback and the note below explains it.
     if(result.status==='ready'&&result.url){
       if(reportTab && !reportTab.closed){
         reportTab.location.href=result.url;
         reportTab.focus();
       } else {
         setPopupBlocked(true);
       }
     }
     // 4) SAVE — one n8n action with the full context: who + what + URL.
     if(result.status==='ready'&&result.url){
       try {
         await saveReportRecord({
           sourceType,
           sourceId: sourceType==='lead'?built.leadId:built.campaignId,
           campaignId: sourceType==='lead'?(built.campaignId||campaign!.id):undefined,
           templateId: templateIdForBackend,
           language, fileUrl: result.url,
           fileId: result.reportId,
           context: {
             user_email: userEmail || '',
             campaign_name: campaign?.name || '',
             lead_name: lead?.leadName || '',
             company_name: lead?.companyName || '',
             campaign_status: campaign?.status || '',
             report_template: activeTemplateId,
           },
         });
         setSaved(true);
       } catch(saveErr){
         // The PDF is safe and downloadable; only the record link is pending.
         // Shown as a quiet note in the job panel — not a hard error.
         setSaveFailed(true);
       }
     }
   } catch(e){
     // Generation failed before a URL existed: the pre-opened blank tab is now
     // dead weight on the user's screen — close it so a failed run doesn't
     // leave an empty tab behind.
     if(reportTab && !reportTab.closed){ try { reportTab.close(); } catch { /* already gone */ } }
     setError(e instanceof Error?e.message:'Generation could not complete.');
   }
   finally{ setPhase('idle'); }
 };

 const translateNote=sourceType==='campaign'
   ? 'Built-in translations. No credits needed.'
   : 'Translate once. Reuse the report with your whole team.';
 return <div className="sql-preview">
  <div className="preview-layout"><aside className="preview-sidebar">
    <header className="preview-sidebar-header">
      <h1>{sourceType==='lead'?'Lead':'Campaign'} report<span>.</span></h1>
      <p className="preview-description">Turn your insights into a report worth sharing.</p>
    </header>
    <button className="preview-button" disabled={busy} onClick={onChooseSellers}><Icon icon="bx:buildings"/>Sellers reports</button>
    {campaign && <div className="preview-source-summary"><span className="preview-source-icon"><Icon icon={glyph(sourceType==='lead'?'user':'flag')} aria-hidden="true"/></span><div><span className="preview-source-label">{leadLocked?'SELECTED LEAD':'CAMPAIGN'}</span><strong>{leadLocked && lead?.leadName ? lead.leadName : campaign.name}</strong>{leadLocked && <span className="preview-source-detail">{campaign.name}</span>}</div></div>}
    {!leadLocked && <>
      {!campaignLocked && <>
        <div className="preview-side-heading">Report for</div>
        <div className="preview-segmented" role="radiogroup" aria-label="Source type">
          <button role="radio" aria-checked={sourceType==='lead'} className={sourceType==='lead'?'selected':''} disabled={busy} onClick={()=>{setSelectedSourceType('lead');setCampaignId('');setLeadId('');}}>Lead</button>
          <button role="radio" aria-checked={sourceType==='campaign'} className={sourceType==='campaign'?'selected':''} disabled={busy} onClick={()=>{setSelectedSourceType('campaign');setCampaignId('');}}>Campaign</button>
        </div>
        <label className="preview-side-heading" htmlFor="rp-campaign">Campaign</label>
      </>}
      {!campaignLocked && <select id="rp-campaign" aria-label="Select campaign" disabled={busy||(campaigns?.length||0)===0} value={campaignId} onChange={e=>{setCampaignId(e.target.value);setLeadId('');}}>
            {(campaigns||[]).map((c:CampaignMetadata)=><option key={c.id} value={c.id}>{c.name}</option>)}
            {(campaigns?.length||0)===0 && <option value="">Loading campaigns…</option>}
          </select>}
      {sourceType==='lead' && <>
        <label className="preview-side-heading" htmlFor="rp-lead">Lead</label>
        <select id="rp-lead" disabled={busy||!campaignId||leads.length===0} value={leadId} onChange={e=>setLeadId(e.target.value)}>
          <option value="">{!campaignId?'Select a campaign first':leads.length===0?'Loading leads…':'Select a lead'}</option>
          {leads.map((l:MinimalLeadData)=><option key={String(l.id)} value={String(l.id)}>{l.leadName}{l.companyName?` · ${l.companyName}`:''}</option>)}
        </select>
      </>}
    </>}
    {sourceType==='lead' && <>
      <div className="preview-rule"/><div className="preview-section-heading"><span>Report type</span><span>01</span></div>
      <div className="preview-templates" role="radiogroup" aria-label="Report type">
        {templateCards.filter(t=>!templateConfigs[t.id]?.deleted).map(t=><button key={t.id} role="radio" aria-checked={activeTemplateId===t.id} className={`preview-template-card${activeTemplateId===t.id?' selected':''}`} disabled={busy} title={t.description} onClick={()=>{sourceType==='lead'?setLeadTemplate(t.id as LeadTemplateId):setCampaignTemplate(t.id as CampaignTemplateId);}}>
          <span className="preview-template-icon"><Icon icon={glyph(t.icon)} aria-hidden="true"/></span>
          <span className="preview-template-name">{t.name}</span>
          <span className="preview-template-indicator" aria-hidden="true">{activeTemplateId===t.id && <Icon icon={glyph('check')}/>}</span>
        </button>)}
      </div>
    </>}
    {sourceType==='campaign' && <>
      <div className="preview-rule"/><div className="preview-section-heading"><span>Report type</span><span>01</span></div>
      <div className="preview-templates" role="radiogroup" aria-label="Report type">
        {templateCards.filter(t=>!templateConfigs[t.id]?.deleted).map(t=><button key={t.id} role="radio" aria-checked={activeTemplateId===t.id} className={`preview-template-card${activeTemplateId===t.id?' selected':''}`} disabled={busy} title={t.description} onClick={()=>setCampaignTemplate(t.id as CampaignTemplateId)}>
          <span className="preview-template-icon"><Icon icon={glyph(t.icon)} aria-hidden="true"/></span>
          <span className="preview-template-name">{t.name}</span>
          <span className="preview-template-indicator" aria-hidden="true">{activeTemplateId===t.id && <Icon icon={glyph('check')}/>}</span>
        </button>)}
      </div>
    </>}
    <div className="preview-rule"/><label className="preview-section-heading" htmlFor="rp-language"><span>Document language</span><span>02</span></label>
    <div className="preview-language-field"><Icon icon={glyph('globe')} aria-hidden="true"/>
    <select id="rp-language" disabled={busy||!sourceReady} value={language} onChange={e=>void chooseLanguage(e.target.value)}>
      {languages.map(l=><option key={l} value={l}>{new Intl.DisplayNames(['en'],{type:'language'}).of(l)||l}</option>)}
    </select>
    </div>
    <p className="preview-help preview-language-note">{translateNote}</p>
    <div className="preview-action-bar">
    {getTemplate(templateIdForBackend)?.conf.kind==='export' ? null : <div className="preview-export-panel"><div className="preview-export-heading"><Icon icon={glyph('file')} aria-hidden="true"/><span>Made to share</span><span>PDF</span></div>
    {job?.status==='ready'&&job.url
      ? <a className="preview-button primary" href={job.url} target="_blank" rel="noopener noreferrer" style={{textDecoration:'none'}}><Icon icon={glyph('download')}/>Open PDF Report</a>
      : <button className="preview-button primary" disabled={!sourceReady||busy||!built} onClick={()=>void generate()}><Icon icon={glyph(busy?'loader-alt':'download')} className={busy?'preview-action-spinner':undefined}/>{phase==='building'?'Loading data…':phase==='translating'?'Translating…':phase==='generating'?'Preparing PDF…':'Download PDF Report'}</button>}
    {/* Result note mirrors what actually happened: tab redirected normally,
        or the browser refused the tab (popupBlocked) — then the "Open PDF
        Report" link above is the one-click fallback and we say so. */}
    <p className="preview-export-note">{popupBlocked?'Your browser blocked the automatic tab — use "Open PDF Report" above.':job?.status==='ready'?'Report opened in a new tab — regenerate any time.':'Preview your report, then download.'}</p></div>}
    {/* DATA EXPORT (campaigns): not a template — a direct download action with
        a format dropdown (Excel / CSV). Opens the export view; the browser's
        save dialog picks the folder. Lives in the sticky action bar so the
        campaign's main actions need no scrolling. */}
    {sourceType==='campaign' && <div className="preview-export-panel">
    <div className="preview-export-heading"><Icon icon={glyph('download')} aria-hidden="true"/><span>Data export</span><span>XLSX · CSV</span></div>
    <div className="relative">
      <button className="preview-button primary" disabled={!sourceReady||exportStatus?.working===true} onClick={()=>setExportDropdownOpen(o=>!o)} style={{textDecoration:'none'}}>
        <Icon icon={glyph(exportStatus?.working===true?'loader-alt':'download')} className={exportStatus?.working===true?'preview-action-spinner':undefined}/>Export data
        <Icon icon={glyph('bxs-chevron-down')} aria-hidden="true" style={{width:14,height:14,opacity:.7,transform:exportDropdownOpen?'rotate(180deg)':'none',transition:'transform .15s'}}/>
      </button>
      {exportDropdownOpen && <div role="menu" aria-label="Export format" className="preview-export-menu">
        <button role="menuitem" disabled={exportStatus?.working===true} onClick={()=>{setExportDropdownOpen(false);runExport('xlsx');}}><Icon icon={glyph('file')} aria-hidden="true"/>Excel (.xlsx)<span>spreadsheet</span></button>
        <button role="menuitem" disabled={exportStatus?.working===true} onClick={()=>{setExportDropdownOpen(false);runExport('csv');}}><Icon icon={glyph('file')} aria-hidden="true"/>CSV<span>universal</span></button>
      </div>}
    </div>
    {exportStatus?.message && <p className="preview-export-note" style={{color:'#8ccfbd'}}>{exportStatus.message}</p>}
    {exportStatus?.error && <p className="preview-export-note" style={{color:'#fcd34d'}}>{exportStatus.error}</p>}
    {!exportStatus?.message && !exportStatus?.error && <p className="preview-export-note">Download the campaign lead data — 50-column standard template.</p>}
    </div>}
    {!currentUser && <p className="preview-access">Sign in to generate reports. Preview, print and HTML download work without a session.</p>}
    {!job && existingLeadReportUrl && sourceType==='lead' && <div className="preview-job" role="status"><strong>Previously generated report</strong><a href={existingLeadReportUrl} target="_blank" rel="noopener noreferrer">Open saved PDF <Icon icon={glyph('link-external')}/></a><span className="preview-help" style={{margin:0}}>Generate a new one above — same settings produce the same cached file.</span></div>}
    {/* Result note only — the primary control above IS the download/open
        button (it auto-opens the PDF), so no second button here. Errors and
        save-fallback warnings still surface inside the sticky bar so the
        user sees them without scrolling. */}
    {job && (job.status!=='ready' || saveFailed || job.error) && <div className="preview-job" role="status">
      {job.status!=='ready' && <strong>Generation failed</strong>}
      {saveFailed && <span className="preview-saved" style={{color:'#fcd34d'}}>PDF opened — the link could not be saved to the record yet. Generating again will update it once saving is available.</span>}
      {job.error && <p>{job.error}</p>}
    </div>}
    {error && <div role="alert" className="preview-error">{error}</div>}{warnings.map(w=><p key={w} className="preview-warning">{w}</p>)}
    </div>
  </aside><section className="preview-stage" aria-label="Report preview">{onBack && <button className="preview-close" onClick={onBack} aria-label="Close the report generator and return to the previous view"><Icon icon={glyph('x')}/>Close</button>}<div className="preview-toolbar"><span><span className="preview-live-dot"/>{!sourceReady?' Select a source':busy?' Working…':phase==='idle'&&translations?' ✓ Translated to '+(new Intl.DisplayNames(['en'],{type:'language'}).of(language)||language):' Live data'}</span><div></div></div><div className={`preview-paper${phase==='translating'||phase==='building'?' is-translating':''}`} aria-busy={phase==='translating'||phase==='building'}><iframe ref={frame} title="Report document preview" srcDoc={displayHtml()} sandbox="allow-same-origin allow-modals" inert={phase==='translating'||phase==='building'}/>{phase==='translating' && <TranslationEffect language={language}/>}{phase==='building' && <TranslationEffect language={language} mode="loading"/>}</div></section></div>
 </div>;

 function displayHtml(): string {
   if(!built)return '';
   try {
     return sourceType==='campaign'
       ? (getTemplate(templateIdForBackend)!.render as any)(built.payload,{language,translations,content:templateConfigs[campaignTemplate]?.campaign,overviewOnly:campaignTemplate==='campaign-overview'})
       : renderSqlReport(built.payload as SqlReport,{language,translations,mql:leadTemplate==='lead-mql',showActivity:!sectionConfig||sectionConfig.sections.includes('activity-timeline'),showSummary:!sectionConfig||sectionConfig.sections.includes('interaction-summary')});
   } catch {
     // Selected language needs translations that aren't ready yet (AI call in
     // flight after a template switch). Render the source-language document
     // behind the translating overlay instead of going blank.
     try {
       return sourceType==='campaign'
         ? (getTemplate(templateIdForBackend)!.render as any)(built.payload,{content:templateConfigs[campaignTemplate]?.campaign,overviewOnly:campaignTemplate==='campaign-overview'})
         : renderSqlReport(built.payload as SqlReport,{mql:leadTemplate==='lead-mql',showActivity:!sectionConfig||sectionConfig.sections.includes('activity-timeline'),showSummary:!sectionConfig||sectionConfig.sections.includes('interaction-summary')});
     } catch { return ''; }
   }
 }
 function renderCurrent(): string { return displayHtml(); }
}
